import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import IziToast from '../IziToast';
import Master from '../Master';
import TextInput from '../TextInput';
import Select from '../Select';
import Button from '../Buttons';
import FilePreview from '../FileUploader/FilePreview';
import Paragraph from '../Paragraph';
import DatePicker from '../DatePicker';
import Collapsible from '../Panels/Collapsible';
import { ValidateEmail } from '../../utils/Functions';

const NewTenant = ({ user, location: { property, source } }) => {
  // get details from local storage
  if (typeof Storage !== 'undefined') {
    if (!property) {
      const propStr = window.localStorage.getItem('property');
      const sourceStr = window.localStorage.getItem('source');
      // eslint-disable-next-line no-param-reassign
      if (propStr) property = JSON.parse(propStr);
      // eslint-disable-next-line no-param-reassign
      if (sourceStr) source = sourceStr;
    }
  }
  const history = useHistory();
  // token
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };
  // refs
  const propertyRef = useRef(null);
  // state
  const [unitNumber, setUnitNumber] = useState('');
  const [unitId, setUnitId] = useState('');
  const [phone, setPhone] = useState('');
  // Optional dedicated WhatsApp number. When set, WhatsApp notifications
  // route here instead of the SMS phone above. Leave blank to disable
  // WhatsApp delivery for this tenant.
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [idOrPassport, setIdOrPassport] = useState('');
  const [nationality, setNationality] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFile, setCurrentFile] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [units, setUnits] = useState([]);
  const propertyName = property ? property.name : '';
  const [startDate, setStartDate] = useState(new Date());
  const [email, setEmail] = useState('');

  // ── Optional Next of Kin (during creation) ─────────────────────────────
  // Users can add one or more during creation; more can also be added later
  // from the Tenant Profile. The backend joi schema requires name, phone,
  // email, relationship to all be present per kin (see joimodels/tenants.js).
  const [kinsList, setKinsList] = useState([]);
  const [kinName, setKinName] = useState('');
  const [kinPhone, setKinPhone] = useState('');
  const [kinEmail, setKinEmail] = useState('');
  const [kinRelationship, setKinRelationship] = useState('');

  // ── Optional CC contact (during creation) ──────────────────────────────
  // Persisted on tenants.other_details.cc_info. WhatsApp uses the dedicated
  // whatsapp_phone (or skips silently when blank). Channels are opt-in.
  const [ccName, setCcName] = useState('');
  const [ccPhone, setCcPhone] = useState('');
  const [ccWhatsappPhone, setCcWhatsappPhone] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [ccSmsEnabled, setCcSmsEnabled] = useState(false);
  const [ccWhatsappEnabled, setCcWhatsappEnabled] = useState(false);
  const [ccEmailEnabled, setCcEmailEnabled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const where = [{ property_id: property.id, status: 'available' }];
        const url = `${process.env.REACT_APP_API_URL
          }units?where=${JSON.stringify(where)}`;
        const {
          data: { response },
        } = await axios.get(url, requestConfig);
        setUnits(response);
      } catch (error) {
        //
      }
    })();
  }, []);

  // Push the current next-of-kin draft into the kins list. Requires all
  // four fields (name, phone, email, relationship) since the API joi
  // schema mandates them per entry.
  const addKin = () => {
    if (!kinName || !kinPhone || !kinEmail || !kinRelationship) {
      IziToast('Fill all four next-of-kin fields before adding');
      return false;
    }
    if (!ValidateEmail(kinEmail)) {
      IziToast('Enter a valid next-of-kin email');
      return false;
    }
    setKinsList((prev) => [...prev, {
      name: kinName.trim(),
      phone: kinPhone.trim(),
      email: kinEmail.trim(),
      relationship: kinRelationship.trim(),
    }]);
    setKinName(''); setKinPhone(''); setKinEmail(''); setKinRelationship('');
    return true;
  };

  const removeKin = (idx) => {
    setKinsList((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveTenant = async () => {
    setIsSubmitting(true);
    let isValid = false;
    let message;
    if (
      unitId &&
      firstName &&
      lastName &&
      phone &&
      nationality &&
      idOrPassport &&
      startDate &&
      unitNumber
    ) {
      if (email !== '') {
        isValid = ValidateEmail(email);
        message = 'Enter a valid email address';
        if(!isValid) { IziToast(message) };
      } else {
        isValid = true;
      }
      if (phone.length !== 12) {
        message = 'Enter a valid phone number';
        IziToast(message);
        isValid = false;
      } else {
        isValid = true;
      }
    } else {
      isValid = false;
      message = 'Enter the red marked fields';
      IziToast(message);
    }
    // ── Optional Next of Kin: auto-include the draft if fully filled ─────
    // The kins list already contains entries the user clicked "Add" on.
    // If the draft row has values, require all four fields (joi schema
    // mandates them) before appending it. Partial drafts block submission
    // so the user doesn't silently lose data.
    let finalKins = [...kinsList];
    const kinDraftHasAny = !!(kinName || kinPhone || kinEmail || kinRelationship);
    if (kinDraftHasAny) {
      if (!kinName || !kinPhone || !kinEmail || !kinRelationship) {
        IziToast('Fill all four next-of-kin fields or click "Add" / clear them');
        isValid = false;
      } else if (!ValidateEmail(kinEmail)) {
        IziToast('Enter a valid next-of-kin email');
        isValid = false;
      } else {
        finalKins = [...finalKins, {
          name: kinName.trim(),
          phone: kinPhone.trim(),
          email: kinEmail.trim(),
          relationship: kinRelationship.trim(),
        }];
      }
    }

    // ── Optional CC contact: validate when any value or channel is on ────
    const ccHasAny = !!(ccName || ccPhone || ccWhatsappPhone || ccEmail
      || ccSmsEnabled || ccWhatsappEnabled || ccEmailEnabled);
    let ccInfo = null;
    if (ccHasAny) {
      if (ccSmsEnabled && !ccPhone.trim()) {
        IziToast('CC: Phone number is required for SMS');
        isValid = false;
      } else if (ccWhatsappEnabled && !ccWhatsappPhone.trim()) {
        IziToast('CC: WhatsApp number is required for the WhatsApp channel');
        isValid = false;
      } else if (ccEmailEnabled && !ccEmail.trim()) {
        IziToast('CC: Email is required for the Email channel');
        isValid = false;
      } else {
        ccInfo = {
          name: ccName.trim(),
          phone: ccPhone.trim(),
          whatsapp_phone: ccWhatsappPhone.trim(),
          email: ccEmail.trim(),
          sms_enabled: ccSmsEnabled,
          whatsapp_enabled: ccWhatsappEnabled,
          email_enabled: ccEmailEnabled,
        };
      }
    }

    if (isValid) {
      const otherDetails = {
        unit_number: unitNumber,
      };
      if (ccInfo) otherDetails.cc_info = ccInfo;
      const stayDuration = 'N/A';
      const formData = new FormData();
      formData.append('unit_id', unitId);
      formData.append('id_or_passport', idOrPassport);
      formData.append('phone', `+${phone}`);
      // WhatsApp number is optional — only send when filled in. Prefixed
      // with '+' the same way as `phone` so the API stores it consistently.
      if (whatsappPhone && whatsappPhone.trim().length > 0) {
        formData.append('whatsapp_phone', `+${whatsappPhone.trim()}`);
      }
      formData.append('last_name', lastName);
      formData.append('first_name', firstName);
      formData.append(
        'start_date',
        Math.round(new Date(startDate).getTime() / 1000.0),
      );
      formData.append('nationality', nationality);
      formData.append('other_details', JSON.stringify(otherDetails));
      if (finalKins.length > 0) {
        formData.append('next_of_kin', JSON.stringify(finalKins));
      }
      formData.append('stay_duration', stayDuration);
      formData.append('email', email);
      // attach image if any
      if (currentFile) {
        formData.append('idphoto', currentFile, currentFile.name);
      }
      const formConfig = {
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Content-Type': 'multipart/form-data',
        },
      };
      IziToast('Saving...');
      try {
        const {
          data: { response },
        } = await axios.post(
          `${process.env.REACT_APP_API_URL}tenants`,
          formData,
          formConfig,
        );
        if (response.id) {
          IziToast('Tenant successfully saved');
          resetFields();
        }
        setIsLoading(!isLoading);
      } catch (error) {
        if (error) {
          if (error.response) {
            if (error.response.data) {
              IziToast(error.response.data.error);
            }
          } else {
            IziToast(error.message);
          }
        } else {
          IziToast('An error occurred');
        }
      }
    }
  };

  const resetFields = () => {
    setUnitNumber('');
    setUnitId('');
    setPhone('');
    setWhatsappPhone('');
    setFirstName('');
    setLastName('');
    setIdOrPassport('');
    setNationality('');
    setIsSubmitting(false);
    setCurrentFile(null);
    setStartDate(new Date());
    setEmail('');
    // Next of kin
    setKinsList([]);
    setKinName(''); setKinPhone(''); setKinEmail(''); setKinRelationship('');
    // CC contact
    setCcName(''); setCcPhone(''); setCcWhatsappPhone(''); setCcEmail('');
    setCcSmsEnabled(false); setCcWhatsappEnabled(false); setCcEmailEnabled(false);
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const { type /* name */ /* size */ } = file;
      const validTypes = ['image/jpeg', 'image/png'];
      if (validTypes.includes(type)) {
        /**
         * TODO : validate duplicate upload
         * message : 'Looks like you have already uploaded this image'
         */
        setCurrentFile(file);
      } else {
        IziToast('Only .jpeg and .png files allowed');
      }
    }
  };

  return (
    <Master
      title="Property Management"
      redirectLink="newtenant"
      source="Add New Tenant"
    >
      <div className="col-xs-3 bm-responsive-div">
        <FilePreview
          currentFile={currentFile}
          onChange={onFileChange}
          inputId="imgTenant"
          onFileRemove={() => {
            setCurrentFile(null);
          }}
          columnWidth={12}
          caption="Upload ID/Passport Image"
        />
        {propertyName && (
          <Paragraph
            columnWidth={12}
            content={propertyName || ''}
            caption="Property Name"
          />
        )}
      </div>
      <div className="col-xs-9 bm-responsive-div">
        <div className="row">
          <Select
            caption="Select Unit Description"
            value={unitId}
            onChange={(event) => {
              setUnitId(event.target.value);
            }}
            columnWidth={8}
            className={isSubmitting && !unitId ? 'error' : ''}
            inputRef={propertyRef}
          >
            <option value="">Select unit description</option>
            {units.map((item) => {
              return (
                <option key={item.id} value={item.id}>
                  {`${item.type} - ${item.description}`}
                </option>
              );
            })}
          </Select>
          <TextInput
            type="text"
            placeholder="Enter unit number"
            caption="Unit Number"
            onChange={(e) => {
              setUnitNumber(e.target.value);
            }}
            value={unitNumber}
            columnWidth={4}
            className={isSubmitting && !unitNumber ? 'error' : ''}
          />
        </div>
        <div className="row">
          <TextInput
            type="text"
            placeholder="Enter first name"
            caption="First Name"
            onChange={(event) => {
              setFirstName(event.target.value);
            }}
            value={firstName}
            columnWidth={3}
            className={isSubmitting && !firstName ? 'error' : ''}
          />
          <TextInput
            type="text"
            placeholder="Enter last name"
            caption="Last Name"
            onChange={(event) => {
              setLastName(event.target.value);
            }}
            value={lastName}
            columnWidth={3}
            className={isSubmitting && !lastName ? 'error' : ''}
          />
          <TextInput
            type="text"
            placeholder="254712345678"
            caption="Phone Number"
            pattern="[0-9]*"
            onChange={(e) => {
              const numbers = /^[0-9\b]+$/;
              const { value } = e.target;
              // allows number only
              if (value === '' || numbers.test(value)) {
                setPhone(value);
              }
            }}
            value={phone}
            columnWidth={3}
            className={isSubmitting && phone.length !== 12 ? 'error' : ''}
          />
          <TextInput
            type="text"
            placeholder="254712345678 (optional)"
            caption="WhatsApp Number"
            pattern="[0-9]*"
            onChange={(e) => {
              const numbers = /^[0-9\b]+$/;
              const { value } = e.target;
              if (value === '' || numbers.test(value)) {
                setWhatsappPhone(value);
              }
            }}
            value={whatsappPhone}
            columnWidth={3}
          />
        </div>
        <div className="row">
          <TextInput
            type="text"
            placeholder="Enter ID/Passport number"
            caption="ID/Passport Number"
            onChange={(e) => {
              setIdOrPassport(e.target.value);
            }}
            value={idOrPassport}
            columnWidth={3}
            className={isSubmitting && !idOrPassport ? 'error' : ''}
          />
          <TextInput
            type="text"
            placeholder="Enter nationality"
            caption="Nationality"
            onChange={(e) => {
              setNationality(e.target.value);
            }}
            value={nationality}
            columnWidth={3}
            className={isSubmitting && !nationality ? 'error' : ''}
          />
          <TextInput
            type="email"
            placeholder="Enter email"
            caption="Email"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            value={email}
            name="email"
            columnWidth={3}
          />
          <DatePicker
            placeholder="Select start date"
            caption="Start Date"
            onChange={(date) => {
              return setStartDate(date);
            }}
            columnWidth={3}
            selected={startDate}
          />
        </div>
      </div>

      {/* ── Optional Next of Kin (collapsible, defaults open) ─────────── */}
      <Collapsible
        caption="Next of Kin (optional)"
        controlId="newtenant_kin"
        columnWidth={12}
      >
        <small style={{ color: '#777', fontStyle: 'italic', display: 'block', marginBottom: 10 }}>
          Add one or more next-of-kin contacts now, or skip and add them later
          from the tenant&apos;s profile. All four fields are required per
          entry.
        </small>
        {kinsList.length > 0 && (
          <div className="table-responsive" style={{ marginBottom: 12 }}>
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Relationship</th>
                  <th style={{ width: 80 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {kinsList.map((k, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`kin-${idx}`}>
                    <td>{k.name}</td>
                    <td>{k.phone}</td>
                    <td>{k.email}</td>
                    <td>{k.relationship}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-default btn-xs"
                        onClick={() => removeKin(idx)}
                      >
                        <i className="fa fa-times" /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row">
          <TextInput
            type="text"
            placeholder="Enter kin name"
            caption="Kin Name"
            onChange={(event) => setKinName(event.target.value)}
            value={kinName}
            columnWidth={3}
          />
          <TextInput
            type="text"
            placeholder="Enter phone number"
            caption="Phone Number"
            onChange={(e) => {
              const numbers = /^[0-9\b]+$/;
              const { value } = e.target;
              if (value === '' || numbers.test(value)) setKinPhone(value);
            }}
            value={kinPhone}
            columnWidth={2}
          />
          <TextInput
            type="text"
            placeholder="Enter email"
            caption="Email"
            onChange={(event) => setKinEmail(event.target.value)}
            value={kinEmail}
            columnWidth={3}
          />
          <TextInput
            type="text"
            placeholder="e.g. Spouse, Parent"
            caption="Relationship"
            onChange={(event) => setKinRelationship(event.target.value)}
            value={kinRelationship}
            columnWidth={2}
          />
          <div className="col-md-2" style={{ marginTop: 24 }}>
            <Button
              caption="Add"
              cssClass="btn-success"
              onClick={addKin}
              type="button"
              isBlack
            />
          </div>
        </div>
      </Collapsible>

      {/* ── Optional CC Contact (collapsible, defaults open) ──────────── */}
      <Collapsible
        caption="CC Contact (optional)"
        controlId="newtenant_cc"
        columnWidth={12}
      >
        <small style={{ color: '#777', fontStyle: 'italic', display: 'block', marginBottom: 10 }}>
          Optional. Send a copy of every invoice and receipt for this tenant to
          another person (e.g. a guarantor, family member, or accountant).
          Toggle the channels they should receive on. WhatsApp uses the
          dedicated number; leave blank to skip WhatsApp.
        </small>
        <div className="row">
          <div className="col-md-3" style={{ marginBottom: 12 }}>
            <label className="control-label">Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter contact name"
              value={ccName}
              onChange={(e) => setCcName(e.target.value)}
            />
          </div>
          <div className="col-md-3" style={{ marginBottom: 12 }}>
            <label className="control-label">Phone Number</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. +254712345678"
              value={ccPhone}
              onChange={(e) => setCcPhone(e.target.value)}
            />
          </div>
          <div className="col-md-3" style={{ marginBottom: 12 }}>
            <label className="control-label">WhatsApp Number (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. +254712345678"
              value={ccWhatsappPhone}
              onChange={(e) => setCcWhatsappPhone(e.target.value)}
            />
          </div>
          <div className="col-md-3" style={{ marginBottom: 12 }}>
            <label className="control-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="e.g. cc@example.com"
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-12" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={ccSmsEnabled}
                onChange={(e) => setCcSmsEnabled(e.target.checked)}
              />
              <i className="fa fa-comment-o" style={{ color: '#34495e' }} />
              <span>Send SMS</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={ccWhatsappEnabled}
                onChange={(e) => setCcWhatsappEnabled(e.target.checked)}
              />
              <i className="fa fa-whatsapp" style={{ color: '#25D366' }} />
              <span>Send WhatsApp</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={ccEmailEnabled}
                onChange={(e) => setCcEmailEnabled(e.target.checked)}
              />
              <i className="fa fa-envelope" style={{ color: '#3498db' }} />
              <span>Send Email</span>
            </label>
          </div>
        </div>
      </Collapsible>

      <div className="right">
        <Button
          caption="<< Back To Tenants"
          cssClass="btn-success"
          onClick={() => {
            history.push({
              pathname: source,
              property,
            });
            if (typeof Storage !== 'undefined') {
              window.localStorage.removeItem('property');
            }
          }}
          type="button"
          isBlack
        />
        <Button
          caption="Create New Tenant"
          cssClass="btn-success"
          onClick={saveTenant}
          type="button"
          isBlack={false}
        />
      </div>
    </Master>
  );
};

const mapStateToProps = (state) => {
  return state;
};

NewTenant.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  location: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

NewTenant.defaultProps = {
  user: {},
  location: {},
};

export default connect(mapStateToProps)(NewTenant);
